
/**
 * @file dcp/src/dcp-client/worker/supervisor2/config.js
 *
 * A support class for Supervisor2.
 * It is for managing dcpConfig.supervisor access.
 *
 * @author Paul, paul@distributive.network
 * @date Feb-April 2023,
 * @module config
 * @copyright Copyright (c) 2018-2025, Distributive Corp. All Rights Reserved
 */
/* global dcpConfig */ // eslint-disable-line no-redeclare
// @ts-check
'use strict';

const { assert } = require('dcp/common/dcp-assert');
const debugging = require('dcp/debugging').scope('worker');
const { ms } = require('dcp/utils');

/**
 * It is important that config properties can be changed dynamically
 * and have the new values reflected in supervisor/worker operations.
 */
class Config
{
  constructor ()
  {
    if (debugging('supervisor'))
    {
      console.debug('sandboxCreationTimeoutMs', Number(this.sandboxConfig?.sandboxCreationTimeoutMs));
      console.debug('progressTimeoutMs', Number(this.sandboxConfig?.progressTimeoutMs));
      console.debug('generalTimeoutMs', Number(this.sandboxConfig?.generalTimeoutMs));
      console.debug('sliceTimeoutMs', Number(this.sandboxConfig?.sliceTimeoutMs));
      console.debug('maxSandboxUse', Number(this.sandboxConfig?.maxSandboxUse));

      console.debug('repoManMultiplier', Number(this.hive.repoMan?.thresholdMultiplier));
      console.debug('repoManFrequencyMs', Number(this.hive.repoMan?.frequencyMs));
      console.debug('runWebGPUBenchmark', Boolean(this.hive.tuning?.runWebGPUBenchmark));

      console.debug('prefetchIntervalMs', ms(Number(this.hive.tuning?.prefetchInterval)));
      console.debug('maxSliceRetries', Number(this.hive.tuning?.maxSandboxSliceRetries));
      console.debug('defaultDelayIncrementMs', Number(this.hive.tuning?.defaultDelayIncrementMs));
      console.debug('maxProportionOfLongJobs', Number(this.hive.tuning?.maxProportionOfLongJobs));
      console.debug('sandboxWorkErrorToWorkEmit', Boolean(this.hive.tuning?.sandboxWorkErrorToWorkEmit));
      console.debug('reservedSliceLifetimeMs', Number(this.hive.tuning?.reservedSliceLifetimeMs));
      console.debug('artifactResolutionTimeoutMs', Number(this.hive.tuning?.artifactResolutionTimeoutMs));
      console.debug('consecutiveAssignFailureLimit', Number(this.sandboxConfig?.consecutiveAssignFailureLimit));
      console.debug('consecutiveSandboxFailureLimit', Number(this.sandboxConfig?.consecutiveSandboxFailureLimit));
      console.debug('sandboxCreationMaxRetry', Number(this.sandboxConfig?.maxRetry));
      console.debug('sendStatusBeginMessage', Boolean(this.hive.tuning?.sendStatusBeginMessage));
      console.debug('-------------------------------------------------------------------------');
      console.debug('sandboxCreationTimeoutMs', this.sandboxCreationTimeoutMs);
      console.debug('progressTimeoutMs', this.progressTimeoutMs);
      console.debug('generalTimeoutMs', this.generalTimeoutMs);
      console.debug('sliceTimeoutMs', this.sliceTimeoutMs);
      console.debug('maxSandboxUse', this.maxSandboxUse);

      console.debug('sandboxConfig', this.sandboxConfig);
      console.debug('connectionOptions', this.connectionOptions);
      console.debug('runWebGPUBenchmark', this.runWebGPUBenchmark);

      console.debug('repoManMultiplier', this.repoManMultiplier);
      console.debug('repoManFrequencyMs', this.repoManFrequencyMs);

      console.debug('prefetchIntervalMs', this.prefetchIntervalMs);
      console.debug('maxSliceRetries', this.maxSliceRetries);
      console.debug('defaultDelayIncrementMs', this.defaultDelayIncrementMs);
      console.debug('maxProportionOfLongJobs', this.maxProportionOfLongJobs);
      console.debug('sandboxWorkErrorToWorkEmit', this.sandboxWorkErrorToWorkEmit);
      console.debug('reservedSliceLifetimeMs', this.reservedSliceLifetimeMs);
      console.debug('artifactResolutionTimeoutMs', this.artifactResolutionTimeoutMs);

      console.debug('detectSuspensionIntervalMs', this.detectSuspensionIntervalMs);
      console.debug('detectSuspensionLateMultiplier', this.detectSuspensionLateMultiplier);
      console.debug('consecutiveAssignFailureLimit', this.consecutiveAssignFailureLimit);
      console.debug('consecutiveSandboxFailureLimit', this.consecutiveSandboxFailureLimit);
      console.debug('sandboxCreationMaxRetry', this.sandboxCreationMaxRetry);
      console.debug('sendStatusBeginMessage', this.sendStatusBeginMessage);
    }
    // Preconditions...
    assert(this.reservedSliceLifetimeMs >= this.sandboxCreationTimeoutMs + 10 * 1000);
  }

  get hive () { return dcpConfig.supervisor; }
  //
  // The dcpConfig.supervisor accessors
  get sandboxCreationTimeoutMs () { return Number(this.sandboxConfig?.sandboxCreationTimeoutMs || ms(2 * 60)); }
  get progressTimeoutMs ()        { return Number(this.sandboxConfig?.progressTimeoutMs || ms(5 * 60)); }
  get generalTimeoutMs ()         { return Number(this.sandboxConfig?.generalTimeoutMs || ms(10)); }
  get sliceTimeoutMs ()           { return Number(this.sandboxConfig?.sliceTimeoutMs || ms(24 * 60 * 60)); }

  get maxSandboxUse ()            { return Number(this.sandboxConfig?.maxSandboxUse ?? 1000); }
  get sandboxConfig ()            { return this.hive.sandbox; }
  get connectionOptions ()        { return this.hive.dcp.connectionOptions; }
  get runWebGPUBenchmark ()       { return Boolean(this.hive.tuning?.runWebGPUBenchmark); }

  get repoManMultiplier ()        { return Number(this.hive.repoMan?.thresholdMultiplier || 2); }
  get repoManFrequencyMs ()       { return Number(this.hive.repoMan?.frequencyMs || ms(60)); }

  get prefetchIntervalMs ()       { return ms(Number(this.hive.tuning?.prefetchInterval ?? 20)); }
  get maxSliceRetries ()          { return Number(this.hive.tuning?.maxSandboxSliceRetries ?? 1); }

  // Note: Timespan for a RESERVED slice to become WORKING -- roughly max timespan to assign sandbox.
  // Must satisfy: this.reservedSliceLifetimeMs >= this.sandboxCreationTimeoutMs + 2 * 60 * 1000
  get reservedSliceLifetimeMs ()  { return Number(this.hive.tuning?.reservedSliceLifetimeMs || ms(7 * 60)); }

  get defaultDelayIncrementMs ()  { return Number(this.hive.tuning?.defaultDelayIncrementMs || 50); }
  get maxProportionOfLongJobs ()  { return Number(this.hive.tuning?.maxProportionOfLongJobs || 0.6); }

  get sandboxWorkErrorToWorkEmit     () { return Boolean(this.hive.tuning?.sandboxWorkErrorToWorkEmit); }
  get artifactResolutionTimeoutMs    () { return Number(this.hive.tuning?.artifactResolutionTimeoutMs || ms(45 * 60)); } // 337MB @ 1Mb/s
  get detectSuspensionIntervalMs     () { return Number(this.hive.tuning?.detectSuspensionIntervalMs || ms(5)); }
  get detectSuspensionLateMultiplier () { return Number(this.hive.tuning?.detectSuspensionLateMultiplier || 2); }

  get sandboxCreationMaxRetry        () { return Number(this.sandboxConfig?.maxRetry ?? 32); }
  get consecutiveAssignFailureLimit  () { return Number(this.sandboxConfig?.consecutiveAssignFailureLimit ?? 3); }
  get consecutiveSandboxFailureLimit () { return Number(this.sandboxConfig?.consecutiveSandboxFailureLimit ?? 3); }
  get sendStatusBeginMessage         () { return Boolean(this.hive.tuning?.sendStatusBeginMessage ?? true); }
}
exports.Config = Config;
